/**
 * UI access rules for the sync/import panel (aligned with Kore API authorization).
 *
 * The sidebar "Synchronisation et import" button is shown when at least one tab
 * is allowed: screenings sync, tickets import, promo-code articles create,
 * article catalogue create, or article stock updates.
 *
 * @typedef {{ can?: (action: string, module: string) => boolean }|null|undefined} AuthUser
 */

/**
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncScreenings(user) {
    return !!user?.can?.('create', 'screenings');
}

/**
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncTickets(user) {
    return !!user?.can?.('create', 'tickets');
}

/**
 * Promo-code import creates articles of type `code_discount`.
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncPromoCodes(user) {
    return !!user?.can?.('create', 'articles');
}

/**
 * Catalogue article import creates and updates `product` articles.
 * Requires create; editOther is needed for update rows (enforced in UI/apply).
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncArticles(user) {
    return !!user?.can?.('create', 'articles');
}

/**
 * Article stock import updates existing availability only.
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncArticleStocks(user) {
    return !!user?.can?.('editOther', 'articles');
}

/**
 * Parent Articles tab (Catalogue and/or Stocks sub-tabs).
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canSyncArticlesTab(user) {
    return canSyncArticles(user) || canSyncArticleStocks(user);
}

/**
 * Whether the sidebar sync/import entry point should be visible.
 * @param {AuthUser} user
 * @returns {boolean}
 */
export function canShowSyncImportPanel(user) {
    return canSyncScreenings(user)
        || canSyncTickets(user)
        || canSyncPromoCodes(user)
        || canSyncArticlesTab(user);
}
